import { IconButton, Menu, Tooltip } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline, PauseCircleIconOutline } from '@neo4j-ndl/react/icons';
import {
  ACTION,
  type AuraDatabase,
  MODAL_TYPE,
  type Project,
  useActiveProject,
  useModal,
  usePermissions,
} from '@nx/state';
import { useRef, useState } from 'react';

const DatabaseActionMenu = ({ database }: { database: AuraDatabase }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const activeProject = useActiveProject();
  const destroyDatabaseModal = useModal(MODAL_TYPE.DESTROY_DATABASE);

  const { isAllowed } = usePermissions();

  const hasDeletePermission = isAllowed(
    ACTION.DELETE,
    `namespaces/${activeProject.id}/aura-instances/${database.instanceId}/aura-databases/${database.id}`,
  );
  const canDeleteDatabase = hasDeletePermission && database.availableActions.delete.enabled;
  const cantDeleteDatabaseTooltip = database.availableActions.delete.enabled
    ? "You don't have permission to perform this action."
    : database.availableActions.delete.message;

  return (
    <>
      <IconButton
        ref={menuRef}
        ariaLabel="More database actions"
        onClick={() => setIsMenuOpen(true)}
        size="small"
        isClean
      >
        <EllipsisHorizontalIconOutline className="h-full w-full" />
      </IconButton>
      <Menu isOpen={isMenuOpen} anchorRef={menuRef} onClose={() => setIsMenuOpen(false)}>
        <Menu.Items>
          <Menu.Item
            title={
              <Tooltip type="simple" isDisabled={canDeleteDatabase}>
                <Tooltip.Trigger hasButtonWrapper>Delete</Tooltip.Trigger>
                <Tooltip.Content>{cantDeleteDatabaseTooltip}</Tooltip.Content>
              </Tooltip>
            }
            className="text-danger-text"
            isDisabled={!canDeleteDatabase}
            onClick={() => {
              destroyDatabaseModal.open({ database, project: activeProject });
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
};

export const DatabaseTableRowActions = ({ database, project }: { database: AuraDatabase; project: Project }) => {
  const { isAllowed } = usePermissions();

  const hasPausePermission = isAllowed(
    ACTION.PAUSE,
    `namespaces/${project.id}/aura-instances/${database.instanceId}/aura-databases/${database.id}`,
  );
  const canPauseDatabase = hasPausePermission && database.availableActions.pause.enabled;
  const cantPauseDatabaseTooltip = database.availableActions.pause.enabled
    ? "You don't have permission to perform this action."
    : database.availableActions.pause.message;

  return (
    <div className="flex flex-grow flex-row items-center justify-end gap-2">
      {database.capabilities.pause.enabled && (
        <Tooltip type="simple" isDisabled={canPauseDatabase}>
          <Tooltip.Trigger hasButtonWrapper>
            <IconButton isDisabled={!canPauseDatabase} isLoading={false} ariaLabel="Pause database" size="small">
              <PauseCircleIconOutline />
            </IconButton>
          </Tooltip.Trigger>
          <Tooltip.Content>{cantPauseDatabaseTooltip}</Tooltip.Content>
        </Tooltip>
      )}
      <DatabaseActionMenu database={database} />
    </div>
  );
};
